import { createSlice } from "@reduxjs/toolkit";
import {
  fetchMessagesByConversationId,
  getChatsById,
  getChats,
  updateConversation,
  createConversation,
  updateFileKey,
} from "./thunks";
import { handleSendWebSocketMessage, upsertChats } from "./helper";
import { showMessageNotification } from "../../utils/notificationService";
import { ROUTES } from "../../constant/routes";

const initialState = {
  isConnected: false,
  lastSocketMessage: null,
  chats: {},
  loading: true,
  selectedChatId: null,
  topicQuestions: null,
  newMessageCount: 0,
};

const chatsSlice = createSlice({
  name: "chats",
  initialState,
  reducers: {
    connectWebSocket: () => {},
    disconnectWebSocket: () => {},
    sendWebSocketMessage: handleSendWebSocketMessage,
    setKeyValue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    removeChat: (state, action) => {
      const { conversation_id } = action.payload;
      if (state.chats[conversation_id]) {
        delete state.chats[conversation_id];
      }
    },
    updateQuestionIndex: (state, action) => {
      const { index, isPrev } = action.payload;
      if (state.topicQuestions?.conversation_id) {
        if (isPrev) {
          state.topicQuestions.prevIndex = index;
        } else state.topicQuestions.currentQuestionIndex = index;
      }
    },
    handlerReceiveMessage: (state, action) => {
      const message = action.payload;
      if (!message?.id) return;

      const { conversation_id, clientMessageId, type } = message;

      const tenantChats = state.chats;
      const chat = tenantChats?.[conversation_id];

      if (chat && type === "updateReaction") {
        chat?.messages?.forEach((m) => {
          if (m.id === message?.id) m.reactions = message?.reactions || [];
        });
        return;
      }

      if (!clientMessageId)
        showMessageNotification(message, () => {
          window.location.pathname = ROUTES.CHATS;
        });

      if (!chat) {
        state.newMessageCount++;
        return;
      }

      chat.last_message = {
        sent_at: message?.sent_at,
        content: message?.content,
        sender_type: message?.sender_type,
        sender_id: message?.sender_id,
      };

      // 🔁 Optimistic update replace (client → server message)
      if (clientMessageId) {
        const index = chat.messages?.findIndex(
          (msg) => msg.id === clientMessageId,
        );

        delete message?.clientMessageId;

        if (index !== -1) chat.messages[index] = message;
        return;
      }

      // ➕ New incoming message
      chat.messages ??= [];
      chat.messages.push(message);
      state.newMessageCount++;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getChats.fulfilled, upsertChats)
      .addCase(getChatsById.fulfilled, upsertChats)
      .addCase(createConversation.fulfilled, upsertChats)
      .addCase(fetchMessagesByConversationId.pending, (state, action) => {
        const { conversation_id } = action.meta.arg;
        state.chats[conversation_id].loading = true;
      })
      .addCase(fetchMessagesByConversationId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { conversation_id, limit } = action.meta.arg;

        const chat = state.chats[conversation_id];

        chat.loading = false;
        chat.messages = [...(data || [])?.reverse(), ...(chat.messages || [])];

        if (data.length < limit) {
          chat.fetchedAll = true;
        }
      })
      .addCase(updateConversation.fulfilled, (state, action) => {
        const { conversation_id, status, remove } =
          action.meta.arg?.payload || {};
        const { data } = action.payload;

        const chat = state.chats[conversation_id];
        if (status) chat.status = status;
        if (data?.length) chat.customers = [...chat?.customers, ...data];
        if (remove)
          chat.customers = chat?.customers?.filter(
            ({ id }) => !remove?.includes(id),
          );
      })
      .addCase(updateFileKey.fulfilled, (state, action) => {
        const { url } = action.payload;
        const { message_id, conversation_id } = action.meta.arg;
        const chat = state.chats[conversation_id];

        if (!chat) return;

        const index = chat?.messages?.findIndex(({ id }) => id === message_id);
        if (index !== -1) {
          chat.messages[index].message_attachment.storage_url = url;
        }
      });
  },
});

export const {
  connectWebSocket,
  disconnectWebSocket,
  sendWebSocketMessage,
  setKeyValue,
  handlerReceiveMessage,
  removeChat,
  updateQuestionIndex,
} = chatsSlice.actions;

export default chatsSlice.reducer;
